/**
 * shipping-threshold.js
 * Converts the free-shipping threshold into the visitor's presentment currency.
 *
 * The threshold is authored in shop currency because that is the currency the shipping
 * rate conditions in Settings > Shipping and delivery are defined in. Liquid exposes no
 * exchange rate to themes, so money filters would relabel 175 as "175 kr" - about 15 EUR
 * against a real 175 EUR rule. Anything rendered by snippets/shipping-claim.liquid in a
 * foreign currency therefore arrives empty and hidden, and is filled in here.
 *
 * Amounts round UP to the nearest 10. Rounding down would advertise a threshold lower
 * than the real one and the checkout would charge shipping anyway, so the error is always
 * pushed to the safe side.
 */
(function () {
  'use strict';

  function convertedAmount(shopAmount, rate) {
    return Math.ceil((shopAmount * rate) / 10) * 10;
  }

  // The drawer observer below watches childList in its subtree, and every textContent
  // write swaps the child text node - a childList mutation. Writing an unchanged value
  // would re-trigger the observer forever, so only write when the text actually differs.
  function setText(el, text) {
    if (el.textContent !== text) el.textContent = text;
  }

  function format(value, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: currency,
        maximumFractionDigits: 0
      }).format(value);
    } catch (e) {
      return value + ' ' + currency;
    }
  }

  function apply(root) {
    var shopify = window.Shopify;
    if (!shopify || !shopify.currency) return;

    var active = shopify.currency.active;
    var rate = parseFloat(shopify.currency.rate || '1');
    if (!active || !isFinite(rate) || rate <= 0) return;

    var scope = root || document;

    var filled = 0;
    scope.querySelectorAll('[data-ship-money][data-ship-convert]').forEach(function (el) {
      var shopAmount = parseFloat(el.getAttribute('data-ship-amount') || '0');
      if (!shopAmount) return;
      var converted = convertedAmount(shopAmount, rate);
      setText(el, format(converted, active));
      el.dataset.shipConverted = String(converted);
      filled++;
    });

    // Reveal as a group. Some claim elements are decoration with no amount inside - the
    // announcement bar's separator, for instance - so they cannot be gated individually.
    // Nothing is revealed unless at least one amount was actually populated.
    if (filled > 0) {
      scope.querySelectorAll('[data-ship-claim][hidden]').forEach(function (claim) {
        claim.removeAttribute('hidden');
      });
    }

    progressBar(scope);
  }

  /**
   * The cart drawer bar compares cart.total_price (presentment cents) against a threshold
   * authored in shop currency, so in a foreign currency the server cannot compute it and
   * renders the bar hidden. Recompute here from the converted amount.
   */
  function progressBar(scope) {
    scope.querySelectorAll('[data-ship-progress]').forEach(function (bar) {
      var money = bar.querySelector('[data-ship-money]');
      var converted = money && money.dataset ? parseFloat(money.dataset.shipConverted || '0') : 0;
      if (!converted) return;

      var totalCents = parseFloat(bar.getAttribute('data-cart-total') || '0');
      var thresholdCents = converted * 100;
      var remaining = thresholdCents - totalCents;
      var pct = Math.min(100, (totalCents / thresholdCents) * 100);

      var fill = bar.querySelector('[data-ship-fill]');
      if (fill) fill.style.width = pct + '%';

      var msg = bar.querySelector('[data-ship-msg]');
      if (msg) {
        setText(msg, remaining > 0
          ? msg.getAttribute('data-away-template').replace('[amount]', format(remaining / 100, window.Shopify.currency.active))
          : msg.getAttribute('data-qualifies-text'));
      }

      // The cart recommendation always shows; only its sentence depends on the
      // gap, which the server cannot compute in this currency.
      var reco = scope.querySelector('[data-ship-reco]');
      if (reco) {
        var head = reco.querySelector('[data-reco-copy]');
        var price = parseFloat(reco.getAttribute('data-reco-price') || '0');
        var text = !head ? null
          : remaining <= 0 ? head.getAttribute('data-pairs-text')
          : price >= remaining ? head.getAttribute('data-closes-text')
          : head.getAttribute('data-closer-text');
        // The copy carries <strong> emphasis, so it is set as markup (theme
        // strings from snippets/ui-string.liquid, never user input). Write only
        // when the state changes: a write is a childList mutation, which
        // re-fires the drawer observer below, and comparing serialised markup
        // is not reliable, so the applied text is remembered instead.
        if (head && text && head.dataset.applied !== text) {
          head.innerHTML = text;
          head.dataset.applied = text;
        }
      }
    });
  }

  function init() { apply(document); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The cart drawer is re-rendered wholesale by the Cart Section Rendering API, which
  // replaces these nodes without a page load.
  var drawer = document.querySelector('[data-cart-drawer]');
  if (drawer && window.MutationObserver) {
    new MutationObserver(function () { apply(drawer); })
      .observe(drawer, { childList: true, subtree: true });
  }

  window.uconShippingThreshold = apply;
})();
